import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Paper,
} from '@mui/material';
import { getFaxAreaCodes, getFaxNumbers, getFacilities, purchaseFaxNumber, searchFaxNumber } from '../../../../utils/apiClient';
import { useTheme } from '../../../../ThemeContext';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';

const BuyFaxNumber = () => {
  const { currentTheme, themeMode } = useTheme();
  const [step, setStep] = useState(1);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaCodes, setAreaCodes] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [areasError, setAreasError] = useState(null);
  const [areaCodeSearch, setAreaCodeSearch] = useState('');

  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [numbersError, setNumbersError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [searchedNumberResult, setSearchedNumberResult] = useState(null);
  const [searchNumberError, setSearchNumberError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // States for number verification before enabling continue
  const [isVerifyingNumber, setIsVerifyingNumber] = useState(false);
  const [isNumberVerified, setIsNumberVerified] = useState(false);
  const [verificationError, setVerificationError] = useState(null);


  // States for step 2
  const [facilities, setFacilities] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState(null);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState({}); // { facilityId: [deptId] }
  const [facilitySearchTerm, setFacilitySearchTerm] = useState('');

  // States for purchase
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [assignmentType, setAssignmentType] = useState('all'); // 'all', 'facility', or 'department'
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');

  const isAllAssigned = useMemo(() => {
    if (facilities.length === 0) return false;
    const allFacilitiesSelected = selectedFacilities.length === facilities.length;
    if (!allFacilitiesSelected) return false;

    return facilities.every(f => {
      const departments = f.departments || [];
      const selected = selectedDepartments[f._id] || [];
      return departments.length === selected.length;
    });
  }, [selectedFacilities, selectedDepartments, facilities]);

  const handleAssignToAllChange = () => {
    if (isAllAssigned) {
      // If everything is selected, clear everything
      setSelectedFacilities([]);
      setSelectedDepartments({});
    } else {
      // If not everything is selected, select everything
      const allFacilityIds = facilities.map(f => f._id);
      setSelectedFacilities(allFacilityIds);
      const allDepts = {};
      facilities.forEach(f => {
        allDepts[f._id] = (f.departments || []).map(d => d._id);
      });
      setSelectedDepartments(allDepts);
    }
  };

  const handleNumberSearch = async () => {
    if (!searchTerm || !selectedArea) {
      setSearchNumberError('Please enter a number to search.');
      return;
    }
    setIsSearching(true);
    setSearchNumberError(null);
    setSearchedNumberResult(null);
    setSelectedNumber(null);
    setIsNumberVerified(false);
    setVerificationError(null);
    
    const fullNumber = `+1${selectedArea.prefix}${searchTerm.replace(/-/g, '')}`;

    try {
      const response = await searchFaxNumber({ faxNumber: fullNumber });
      if (response.status === 0) { // Not available
        setSearchNumberError(response.message);
        // Keep showing the original list
        setSearchedNumberResult({ available: false });
      } else { // Available
        setSearchedNumberResult({ available: true, number: fullNumber.slice(2) }); // store without +1
      }
    } catch (error) {
      setSearchNumberError(error.message);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handle number selection with verification
   * When user selects a number, verify its availability via /fax/ifax/search
   * before enabling the Continue button
   */
  const handleSelectNumber = async (number) => {
    // Set the selected number immediately for UI feedback
    setSelectedNumber(number);
    setIsNumberVerified(false);
    setVerificationError(null);
    setIsVerifyingNumber(true);

    try {
      // Format the number for the API call
      // Number format from list might be like "9725551234" (without +1)
      const fullNumber = number.startsWith('+1') ? number : `+1${number}`;
      
      const response = await searchFaxNumber({ faxNumber: fullNumber });
      
      if (response.status === 0) {
        // Number is not available
        setVerificationError(response.message || 'Number is no longer available');
        setIsNumberVerified(false);
      } else {
        // Number is available
        setIsNumberVerified(true);
        setVerificationError(null);
      }
    } catch (error) {
      setVerificationError(error.message || 'Failed to verify number availability');
      setIsNumberVerified(false);
    } finally {
      setIsVerifyingNumber(false);
    }
  };


  // Fetch facilities for step 2
  useEffect(() => {
    const fetchFacilities = async () => {
      if (step === 2 && facilities.length === 0) {
        setLoadingFacilities(true);
        setFacilitiesError(null);
        try {
          const response = await getFacilities({ includeDepartments: true, include_inactive: false });
          if (Array.isArray(response)) {
            // Filter to only show active facilities with active departments
            const activeFacilities = response
              .filter(facility => facility.isActive !== false)
              .map(facility => ({
                ...facility,
                // Filter departments to only include active ones
                departments: (facility.departments || []).filter(dept => dept.isActive !== false)
              }));
            setFacilities(activeFacilities);
          } else {
            setFacilitiesError('Could not load facilities.');
          }
        } catch (err) {
          setFacilitiesError(err.message);
        } finally {
          setLoadingFacilities(false);
        }
      }
    };
    fetchFacilities();
  }, [step, facilities]);

  useEffect(() => {
    // Clear search results when area changes
    setSearchTerm('');
    setSearchedNumberResult(null);
    setSearchNumberError(null);
  }, [selectedArea]);

  useEffect(() => {
    const fetchAreaCodes = async () => {
      setLoadingAreas(true);
      setAreasError(null);
      try {
        const response = await getFaxAreaCodes();
        if (Array.isArray(response)) {
          setAreaCodes(response);
        } else {
          setAreasError('Failed to fetch area codes');
        }
      } catch (err) {
        setAreasError(err.message);
      } finally {
        setLoadingAreas(false);
      }
    };

    fetchAreaCodes();
  }, []);

  const fetchNumbers = async (area) => {
    if (!area) return;

    setLoadingNumbers(true);
    setNumbersError(null);
    setAvailableNumbers([]);
    setSelectedNumber(null);
    setSearchedNumberResult(null);
    setSearchNumberError(null);
    setIsNumberVerified(false);
    setVerificationError(null);

    try {
      const response = await getFaxNumbers(area.id);
      if (Array.isArray(response) && response.length > 0) {
        setAvailableNumbers(response);
      } else if (response?.status === 1 && response?.data?.length > 0) {
        // Fallback for old response format
        setAvailableNumbers(response.data);
      } else {
        setNumbersError(response?.message || 'No numbers available for this area.');
      }
    } catch (err) {
      console.error('Error fetching fax numbers:', err);
      setNumbersError(err.message);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleAreaChange = (event) => {
    const areaId = event.target.value;
    const area = areaCodes.find(a => a.id === areaId);
    setSelectedArea(area);
    fetchNumbers(area);
  };

  const handleContinue = () => {
    setStep(2);
  };

  const handleContinueToStep3 = () => {
    setStep(3);
  };

  // Reset selections when step changes from 2 to 1 or when selected number changes
  useEffect(() => {
    if (step === 1) {
      setSelectedFacilities([]);
      setSelectedDepartments({});
    }
  }, [step, selectedNumber]);

  const filteredNumbers = useMemo(() => {
    if (searchedNumberResult && searchedNumberResult.available) {
      return [searchedNumberResult.number];
    }
    return availableNumbers;
  }, [availableNumbers, searchedNumberResult]);

  // Fetch departments when a facility is selected
  const handleToggleFacility = (facilityId) => {
    const currentIndex = selectedFacilities.indexOf(facilityId);
    const newSelected = [...selectedFacilities];

    if (currentIndex === -1) {
      // Facility is being selected - ensure no stale departments remain
      newSelected.push(facilityId);
      const newSelectedDepts = { ...selectedDepartments };
      // Clear any existing departments for this facility when selecting
      if (newSelectedDepts[facilityId]) {
        delete newSelectedDepts[facilityId];
        setSelectedDepartments(newSelectedDepts);
      }
    } else {
      // Facility is being deselected - remove departments for this facility
      newSelected.splice(currentIndex, 1);
      const newSelectedDepts = { ...selectedDepartments };
      delete newSelectedDepts[facilityId];
      setSelectedDepartments(newSelectedDepts);
    }
    setSelectedFacilities(newSelected);
  };

  const handleToggleDepartment = (facilityId, deptId) => {
    const depts = selectedDepartments[facilityId] || [];
    const currentIndex = depts.indexOf(deptId);
    const newDepts = [...depts];

    if (currentIndex === -1) {
      newDepts.push(deptId);
    } else {
      newDepts.splice(currentIndex, 1);
    }
    setSelectedDepartments({ ...selectedDepartments, [facilityId]: newDepts });
  };

  const handleSelectAllDepartments = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    if (facility && facility.departments) {
      const allDeptIds = facility.departments.map(d => d._id);
      setSelectedDepartments({ ...selectedDepartments, [facilityId]: allDeptIds });
    }
  };

  const handleClearDepartments = (facilityId) => {
    setSelectedDepartments({ ...selectedDepartments, [facilityId]: [] });
  };

  const handleSelectAllFacilities = () => {
    if (allFacilitiesSelected) {
      // Deselecting all facilities - clear all departments
      setSelectedFacilities([]);
      setSelectedDepartments({});
    } else {
      const allFacilityIds = filteredFacilities.map(f => f._id);
      setSelectedFacilities(allFacilityIds);
    }
  };

  const handleSelectAllVisibleDepartments = () => {
    const allSelected = areAllVisibleDepartmentsSelected();
    let newSelectedDepartments = { ...selectedDepartments };

    if (allSelected) {
      // Clear all visible
      selectedFacilities.forEach(facilityId => {
        newSelectedDepartments[facilityId] = [];
      });
    } else {
      // Select all visible
      selectedFacilities.forEach(facilityId => {
        const facility = facilities.find(f => f._id === facilityId);
        const depts = facility?.departments || [];
        if (depts.length > 0) {
          newSelectedDepartments[facilityId] = depts.map(d => d._id);
        }
      });
    }
    setSelectedDepartments(newSelectedDepartments);
  };

  const filteredFacilities = facilities.filter(facility =>
    facility.facilityName.toLowerCase().includes(facilitySearchTerm.toLowerCase())
  );

  const allFacilitiesSelected = filteredFacilities.length > 0 && selectedFacilities.length === filteredFacilities.length;

  const areAllVisibleDepartmentsSelected = () => {
    if (selectedFacilities.length === 0) return false;
    return selectedFacilities.every(facilityId => {
      const facility = facilities.find(f => f._id === facilityId);
      const departments = facility?.departments || [];
      const selectedDepts = selectedDepartments[facilityId] || [];
      if (departments.length === 0) return true;
      return departments.length === selectedDepts.length;
    });
  };

  const allVisibleDepartmentsSelected = areAllVisibleDepartmentsSelected();

  const handlePurchase = async () => {
    try {
      setPurchasing(true);
      setPurchaseError(null);

      const customerId = sessionStorage.getItem('customerId');
      let associatedTo = null;
      let isDefaultFax = false;

      if (assignmentType === 'all') {
        isDefaultFax = true;
      } else if (assignmentType === 'facility') {
        associatedTo = { type: 'facility', id: selectedFacility };
      } else if (assignmentType === 'department') {
        associatedTo = { type: 'department', id: selectedDepartment };
      }

      // Normalize fax number: remove any existing +1 or + prefix, then add +1
      const normalizedFaxNumber = selectedNumber.startsWith('+1') 
        ? selectedNumber 
        : selectedNumber.startsWith('+') 
          ? `+1${selectedNumber.slice(1)}`
          : `+1${selectedNumber}`;

      const requestBody = {
        faxNumber: normalizedFaxNumber,
        customerId: customerId,
        associatedTo: associatedTo,
        isDefaultFax: isDefaultFax,
      };

      const response = await purchaseFaxNumber(requestBody);

      if (response.success) {
        setStep(4);
      } else {
        setPurchaseError(response.message || 'Failed to purchase fax number');
      }
    } catch (error) {
      setPurchaseError(error.message || 'Failed to purchase fax number. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepOne
            currentTheme={currentTheme}
            themeMode={themeMode}
            areasError={areasError}
            loadingAreas={loadingAreas}
            selectedArea={selectedArea}
            handleAreaChange={handleAreaChange}
            areaCodes={areaCodes}
            areaCodeSearch={areaCodeSearch}
            setAreaCodeSearch={setAreaCodeSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filteredNumbers={filteredNumbers}
            loadingNumbers={loadingNumbers}
            numbersError={numbersError}
            selectedNumber={selectedNumber}
            setSelectedNumber={handleSelectNumber}
            handleContinue={handleContinue}
            handleNumberSearch={handleNumberSearch}
            isSearching={isSearching}
            searchNumberError={searchNumberError}
            searchedNumberResult={searchedNumberResult}
            isVerifyingNumber={isVerifyingNumber}
            isNumberVerified={isNumberVerified}
            verificationError={verificationError}
          />
        );
      case 2:
        return (
          <StepTwo
            currentTheme={currentTheme}
            themeMode={themeMode}
            setStep={setStep}
            assignmentType={assignmentType}
            setAssignmentType={setAssignmentType}
            facilities={facilities}
            loadingFacilities={loadingFacilities}
            facilitiesError={facilitiesError}
            selectedFacility={selectedFacility}
            setSelectedFacility={setSelectedFacility}
            selectedDepartment={selectedDepartment}
            setSelectedDepartment={setSelectedDepartment}
            handleContinue={handleContinueToStep3}
          />
        );
      case 3:
        return (
          <StepThree
            assignmentType={assignmentType}
            selectedFacility={selectedFacility}
            selectedDepartment={selectedDepartment}
            facilities={facilities}
            themeMode={themeMode}
            currentTheme={currentTheme}
            selectedNumber={selectedNumber}
            purchaseError={purchaseError}
            purchasing={purchasing}
            setStep={setStep}
            handlePurchase={handlePurchase}
          />
        );
      case 4:
        return (
          <StepFour
            currentTheme={currentTheme}
            themeMode={themeMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{
        p: 4,
        backgroundColor: 'transparent',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems={step === 4 ? 'center' : 'start'}
          gap={1.5}
          sx={{
            maxWidth: step === 1 ? 450 : '100%',
            width: '100%',
            flex: 1,
            overflow: 'hidden'
          }}
        >
          {renderStep()}
        </Box>
      </Paper>
    </Container>
  );
};

export default BuyFaxNumber;

